import { Phone, Truck, Cpu, GraduationCap, Clock, ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const STEPS = [{
  Icon: Phone,
  time: "2 min",
  timeLabel: "phone call",
  step: "01",
  title: "Tell us your motor",
  desc: "Quick call or WhatsApp — name of the brand and how many remotes you need. If you're not sure, send a photo of the motor box.",
  bullets: ["Free phone diagnosis", "Photo via WhatsApp OK"]
}, {
  Icon: Truck,
  time: "Same day",
  timeLabel: "to your door",
  step: "02",
  title: "We bring the right gear",
  desc: "Brand-matched remote, keypad, or universal — straight off our van. We carry stock for all 8 major Aussie brands so we don't leave empty-handed.",
  bullets: ["Stocked on the van", "All major brands"]
}, {
  Icon: Cpu,
  time: "5–15 min",
  timeLabel: "on-site",
  step: "03",
  title: "Pair, test, erase old",
  desc: "We program every remote, sync keypads, test the door from the driveway, and wipe any old / unknown remotes from the motor's memory.",
  bullets: ["Test from driveway", "Old remotes wiped"]
}, {
  Icon: GraduationCap,
  time: "3 min",
  timeLabel: "walk-through",
  step: "04",
  title: "Show you how",
  desc: "Quick walkthrough of how to add another remote later, how to change the keypad PIN, and how to factory-reset if the door changes hands.",
  bullets: ["Add remotes yourself", "PIN-change guide"]
}];
export default function RemoteProcess() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Ambient radial */}
      <div aria-hidden className="absolute inset-0" style={{
      background: `radial-gradient(circle at 80% 20%, ${NAVY_LIGHT}66 0%, ${NAVY} 50%), radial-gradient(circle at 10% 100%, ${GOLD}10 0%, transparent 40%)`
    }} />

      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />

      {/* Watermark */}
      <div aria-hidden className="absolute pointer-events-none select-none" style={{
      right: "-30px",
      top: "60px",
      fontFamily: FONT_HEAD,
      fontSize: "240px",
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.05em",
      color: `${GOLD}12`
    }}>
        05
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD
          }}>
              05 · The 20-Minute Visit
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: "#ffffff",
          marginBottom: "20px"
        }}>
            Four steps. One coffee.
            <br />
            <span style={{
            color: GOLD
          }}>You're back to clicking.</span>
          </h2>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "17px",
          lineHeight: 1.65,
          color: "rgba(255,255,255,0.72)",
          maxWidth: "640px"
        }}>
            Most remote / keypad jobs are done before you've finished your
            flat white. Here's exactly how a visit runs.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical gold line (mobile) / horizontal (desktop) */}
          <div aria-hidden className="hidden lg:block absolute top-[44px] left-0 right-0 h-px" style={{
          background: `linear-gradient(90deg, ${GOLD}00 0%, ${GOLD}88 8%, ${GOLD}88 92%, ${GOLD}00 100%)`
        }} />

          <div className="grid lg:grid-cols-4 gap-6 lg:gap-4">
            {STEPS.map(({
            Icon,
            time,
            timeLabel,
            step,
            title,
            desc,
            bullets
          }, i) => <div key={step} className="relative">
                  {/* Step circle */}
                  <div className="relative mb-7 flex items-center justify-center lg:justify-start">
                    <div className="relative flex items-center justify-center rounded-full" style={{
                width: 88,
                height: 88,
                background: `linear-gradient(135deg, ${NAVY_LIGHT} 0%, ${NAVY_DEEP} 100%)`,
                border: `2px solid ${GOLD}`,
                boxShadow: `0 0 0 6px ${NAVY}, 0 0 30px ${GOLD}33`
              }}>
                      <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                        <Icon size={32} color={GOLD} strokeWidth={2} />
                      </span>

                      {/* Step number badge */}
                      <span className="absolute" style={{
                  top: "-8px",
                  right: "-8px",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: GOLD,
                  color: NAVY,
                  fontFamily: FONT_HEAD,
                  fontSize: "12px",
                  fontWeight: 900,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  letterSpacing: "-0.02em",
                  boxShadow: `0 4px 12px ${GOLD}55`
                }}>
                        {step}
                      </span>
                    </div>
                  </div>

                  {/* Time pill */}
                  <div className="inline-flex items-center gap-1.5 mb-4 px-3 py-1.5 rounded-full" style={{
              backgroundColor: `${GOLD}15`,
              border: `1px solid ${GOLD}33`
            }}>
                    <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                      <Clock size={11} color={GOLD} strokeWidth={2.5} />
                    </span>
                    <span style={{
                fontFamily: FONT_HEAD,
                fontSize: "11px",
                fontWeight: 700,
                color: GOLD,
                letterSpacing: "0.05em"
              }}>
                      {time}
                    </span>
                    <span style={{
                fontFamily: FONT_BODY,
                fontSize: "10.5px",
                color: "rgba(255,255,255,0.55)",
                textTransform: "uppercase",
                letterSpacing: "0.1em"
              }}>
                      · {timeLabel}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "1.25rem",
              lineHeight: 1.2,
              color: "#ffffff",
              marginBottom: "10px",
              letterSpacing: "-0.015em"
            }}>
                    {title}
                  </h3>

                  {/* Desc */}
                  <p style={{
              fontFamily: FONT_BODY,
              fontSize: "14px",
              lineHeight: 1.6,
              color: "rgba(255,255,255,0.7)",
              marginBottom: "14px"
            }}>
                    {desc}
                  </p>

                  {/* Bullets */}
                  <ul className="space-y-1.5">
                    {bullets.map(b => <li key={b} className="flex items-start gap-2" style={{
                fontFamily: FONT_BODY,
                fontSize: "12.5px",
                color: "rgba(255,255,255,0.78)",
                fontWeight: 500
              }}>
                        <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex flex-shrink-0 mt-0.5">
                          <CheckCircle2 size={13} color={GOLD} strokeWidth={2.5} />
                        </span>
                        <span>{b}</span>
                      </li>)}
                  </ul>
                </div>)}
          </div>
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-16 rounded-2xl p-7 md:p-8 relative overflow-hidden" style={{
        background: `linear-gradient(135deg, ${NAVY_LIGHT}AA 0%, ${NAVY_DEEP} 100%)`,
        border: `1px solid ${GOLD}33`
      }}>
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-lg flex-shrink-0" style={{
              width: 52,
              height: 52,
              backgroundColor: `${GOLD}20`,
              border: `1px solid ${GOLD}44`
            }}>
                <Clock size={22} color={GOLD} strokeWidth={2.5} />
              </span>
              <div>
                <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: GOLD,
                marginBottom: "4px"
              }}>
                  Available today
                </div>
                <h3 style={{
                fontFamily: FONT_HEAD,
                fontWeight: 800,
                fontSize: "clamp(1.3rem, 2vw, 1.7rem)",
                lineHeight: 1.2,
                color: "#ffffff",
                letterSpacing: "-0.015em"
              }}>
                  Call by 2pm — synced by sundown.
                </h3>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 flex-shrink-0">
              <a href="tel:0485000933" className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full transition-all hover:scale-[1.02]" style={{
              backgroundColor: GOLD,
              color: NAVY,
              fontFamily: FONT_HEAD,
              fontWeight: 700,
              fontSize: "14px",
              boxShadow: `0 8px 24px ${GOLD}33`
            }}>
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <Phone size={16} strokeWidth={2.5} />
                </span>
                Book me in
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <ArrowRight size={14} strokeWidth={2.5} />
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />
    </section>;
}